import React, { useState, useEffect } from 'react';

import { formatPortalDate } from '../../utils/dateUtils';

const getApiBase = () =>
  process.env.REACT_APP_API_URL || 'http://localhost/api';

// Defined locally — avoids circular imports with StaffRequestPortal
export interface SubmittedRequest {
  referenceNumber: string;
  email: string;
  purpose: string;
  destination: string;
  startDateTime: string;
  endDateTime: string;
}

interface FormData {
  requesterName: string;
  requesterEmail: string;
  requesterPhone: string;
  department: string;
  purpose: string;
  destination: string;
  startDateTime: string;
  endDateTime: string;
  passengerCount: number;
  vehicleCategory: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  specialRequirements: string;
}

interface AvailabilityInfo {
  available: boolean;
  vehicleCount: number;
  message: string;
}

interface Props {
  companyDomain: string;
  onSuccess: (request: SubmittedRequest) => void;
  onBack: () => void;
}

const DEPARTMENTS = [
  'Administration', 'Finance', 'Operations', 'Human Resources',
  'Information Technology', 'Marketing', 'Sales', 'Legal',
  'Customer Service', 'Logistics', 'Executive', 'Other',
];

const VEHICLE_CATEGORIES = [
  { value: 'any', label: 'Any Available', desc: 'System assigns best fit' },
  { value: 'executive', label: 'Executive Pool', desc: 'Premium vehicles for senior staff' },
  { value: 'staff_bus', label: 'Staff Bus', desc: 'For larger groups' },
  { value: 'technical', label: 'Technical Unit', desc: 'Equipped for field work' },
  { value: 'general', label: 'General Pool', desc: 'Standard vehicles' },
];

const STEPS = ['Your Details', 'Trip Info', 'Review'];

const PublicRequestForm: React.FC<Props> = ({ companyDomain, onSuccess, onBack }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityInfo | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const [form, setForm] = useState<FormData>({
    requesterName: '', requesterEmail: '', requesterPhone: '', department: '',
    purpose: '', destination: '', startDateTime: '', endDateTime: '',
    passengerCount: 1, vehicleCategory: 'any', priority: 'medium', specialRequirements: '',
  });

  const set = (field: keyof FormData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  useEffect(() => {
    if (step === 1 && form.startDateTime && form.endDateTime) checkAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startDateTime, form.endDateTime, form.vehicleCategory, step]);

  const checkAvailability = async () => {
    if (!form.startDateTime || !form.endDateTime) return;
    setCheckingAvailability(true);
    try {
      const params = new URLSearchParams({ start: form.startDateTime, end: form.endDateTime, category: form.vehicleCategory });
      const res = await fetch(`${getApiBase()}/public/${companyDomain}/availability?${params}`);
      const data = await res.json();
      if (data.success) {
        setAvailability({
          available: data.data.vehicleCount > 0,
          vehicleCount: data.data.vehicleCount,
          message: data.data.vehicleCount > 0
            ? `${data.data.vehicleCount} vehicle(s) available for this time slot`
            : 'No vehicles available for this time. You can still submit — fleet manager will review.',
        });
      }
    } catch { setAvailability(null); }
    finally { setCheckingAvailability(false); }
  };

  const validateStep = (s: number): boolean => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (s === 0) {
      if (!form.requesterName.trim()) errs.requesterName = 'Full name is required';
      if (!form.requesterEmail.trim()) errs.requesterEmail = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(form.requesterEmail)) errs.requesterEmail = 'Enter a valid email';
      if (!form.requesterPhone.trim()) errs.requesterPhone = 'Phone number is required';
      if (!form.department) errs.department = 'Department is required';
    }
    if (s === 1) {
      if (!form.purpose.trim()) errs.purpose = 'Purpose is required';
      if (!form.destination.trim()) errs.destination = 'Destination is required';
      if (!form.startDateTime) errs.startDateTime = 'Start date & time is required';
      else if (new Date(form.startDateTime) < new Date(Date.now() + 60 * 60 * 1000))
        errs.startDateTime = 'Start time must be at least 1 hour from now';
      if (!form.endDateTime) errs.endDateTime = 'End date & time is required';
      else if (form.startDateTime && new Date(form.endDateTime) <= new Date(form.startDateTime))
        errs.endDateTime = 'End time must be after start time';
      if (form.passengerCount < 1) errs.passengerCount = 'At least 1 passenger';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => { if (validateStep(step)) setStep(s => s + 1); };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    setLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/public/${companyDomain}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          startDateTime: new Date(form.startDateTime).toISOString(),
          endDateTime: new Date(form.endDateTime).toISOString(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        onSuccess({
          referenceNumber: data.data.referenceNumber,
          email: form.requesterEmail,
          purpose: form.purpose,
          destination: form.destination,
          startDateTime: form.startDateTime,
          endDateTime: form.endDateTime,
        });
      } else {
        setErrors({ purpose: data.message || 'Submission failed. Please try again.' });
      }
    } catch { setErrors({ purpose: 'Network error. Please check your connection.' }); }
    finally { setLoading(false); }
  };

  const fmt = (dt: string) => dt
    ? new Date(dt).toLocaleString('en-NG', { timeZone: 'Africa/Lagos', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  const minDateTime = new Date(Date.now() + 60 * 60 * 1000)
    .toLocaleString('sv', { timeZone: 'Africa/Lagos' })
    .replace(' ', 'T')
    .slice(0, 16);

  return (
    <div className="form-view">
      <button className="back-btn" onClick={step === 0 ? onBack : () => setStep(s => s - 1)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        {step === 0 ? 'Back' : 'Previous'}
      </button>

      <div className="step-indicator">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className={`step-dot ${i === step ? 'active' : i < step ? 'done' : ''}`}>
              {i < step
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5 9-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
                : <span>{i + 1}</span>}
            </div>
            {i < STEPS.length - 1 && <div className={`step-line ${i < step ? 'done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>
      <p className="step-label-current">{STEPS[step]}</p>

      {step === 0 && (
        <div className="form-fields">
          <div className="field-group">
            <label className="field-label">Full Name *</label>
            <input className={`field-input ${errors.requesterName ? 'error' : ''}`} type="text" placeholder="e.g. Amaka Johnson" value={form.requesterName} onChange={e => set('requesterName', e.target.value)} />
            {errors.requesterName && <span className="field-error">{errors.requesterName}</span>}
          </div>
          <div className="field-group">
            <label className="field-label">Email Address *</label>
            <input className={`field-input ${errors.requesterEmail ? 'error' : ''}`} type="email" placeholder="you@company.com" value={form.requesterEmail} onChange={e => set('requesterEmail', e.target.value)} />
            {errors.requesterEmail && <span className="field-error">{errors.requesterEmail}</span>}
            <span className="field-hint">Trip updates and approval will be sent here</span>
          </div>
          <div className="field-group">
            <label className="field-label">Phone Number *</label>
            <input className={`field-input ${errors.requesterPhone ? 'error' : ''}`} type="tel" placeholder="e.g. 08012345678" value={form.requesterPhone} onChange={e => set('requesterPhone', e.target.value)} />
            {errors.requesterPhone && <span className="field-error">{errors.requesterPhone}</span>}
          </div>
          <div className="field-group">
            <label className="field-label">Department *</label>
            <select className={`field-select ${errors.department ? 'error' : ''}`} value={form.department} onChange={e => set('department', e.target.value)}>
              <option value="">Select your department</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {errors.department && <span className="field-error">{errors.department}</span>}
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="form-fields">
          <div className="field-group">
            <label className="field-label">Purpose of Trip *</label>
            <textarea className={`field-textarea ${errors.purpose ? 'error' : ''}`} placeholder="e.g. Client meeting at Head Office, site inspection..." rows={3} value={form.purpose} onChange={e => set('purpose', e.target.value)} />
            {errors.purpose && <span className="field-error">{errors.purpose}</span>}
          </div>
          <div className="field-group">
            <label className="field-label">Destination *</label>
            <input className={`field-input ${errors.destination ? 'error' : ''}`} type="text" placeholder="e.g. Victoria Island, Lagos" value={form.destination} onChange={e => set('destination', e.target.value)} />
            {errors.destination && <span className="field-error">{errors.destination}</span>}
          </div>
          <div className="field-row">
            <div className="field-group">
              <label className="field-label">Start Date & Time *</label>
              <input className={`field-input ${errors.startDateTime ? 'error' : ''}`}
                type="datetime-local"
                min={minDateTime}
                step="1800"
                value={form.startDateTime}
                onChange={e => set('startDateTime', e.target.value)} />

              {errors.startDateTime && <span className="field-error">{errors.startDateTime}</span>}
            </div>
            <div className="field-group">
              <label className="field-label">End Date & Time *</label>
              <input className={`field-input ${errors.endDateTime ? 'error' : ''}`}
                type="datetime-local"
                min={form.startDateTime || minDateTime}
                step="1800"
                value={form.endDateTime}
                onChange={e => set('endDateTime', e.target.value)} />
              {errors.endDateTime && <span className="field-error">{errors.endDateTime}</span>}
            </div>
          </div>
          <div className="field-row">
            <div className="field-group">
              <label className="field-label">Passengers</label>
              <input className="field-input" type="number" min={1} max={50} value={form.passengerCount} onChange={e => set('passengerCount', parseInt(e.target.value) || 1)} />
            </div>
            <div className="field-group">
              <label className="field-label">Priority</label>
              <select className="field-select" value={form.priority} onChange={e => set('priority', e.target.value as any)}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="field-group">
            <label className="field-label">Vehicle Preference</label>
            <div className="category-grid">
              {VEHICLE_CATEGORIES.map(cat => (
                <button key={cat.value} type="button" className={`category-option ${form.vehicleCategory === cat.value ? 'selected' : ''}`} onClick={() => set('vehicleCategory', cat.value)}>
                  <span className="category-label">{cat.label}</span>
                  <span className="category-desc">{cat.desc}</span>
                </button>
              ))}
            </div>
          </div>
          {checkingAvailability && <div className="availability-checking"><div className="spinner-sm" /><span>Checking availability...</span></div>}
          {!checkingAvailability && availability && (
            <div className={`availability-badge ${availability.available ? 'available' : 'limited'}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                {availability.available
                  ? <path d="M5 12l5 5 9-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  : <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
              </svg>
              <span>{availability.message}</span>
            </div>
          )}
          <div className="field-group">
            <label className="field-label">Special Requirements <span className="optional">(optional)</span></label>
            <textarea className="field-textarea" placeholder="e.g. Need AC, wheelchair accessible, specific driver..." rows={2} value={form.specialRequirements} onChange={e => set('specialRequirements', e.target.value)} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="review-view">
          <div className="review-section">
            <h3 className="review-section-title">Your Details</h3>
            <div className="review-grid">
              <RR label="Name" value={form.requesterName} />
              <RR label="Email" value={form.requesterEmail} />
              <RR label="Phone" value={form.requesterPhone} />
              <RR label="Department" value={form.department} />
            </div>
          </div>
          <div className="review-section">
            <h3 className="review-section-title">Trip Details</h3>
            <div className="review-grid">
              <RR label="Purpose" value={form.purpose} />
              <RR label="Destination" value={form.destination} />
              <RR label="Start" value={fmt(form.startDateTime)} />
              <RR label="End" value={fmt(form.endDateTime)} />
              <RR label="Passengers" value={String(form.passengerCount)} />
              <RR label="Vehicle" value={VEHICLE_CATEGORIES.find(c => c.value === form.vehicleCategory)?.label || 'Any'} />
              <RR label="Priority" value={form.priority} capitalize />
              {form.specialRequirements && <RR label="Requirements" value={form.specialRequirements} />}
            </div>
          </div>
          <div className="review-notice">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p>By submitting, you confirm all details are correct. You'll receive a confirmation at <strong>{form.requesterEmail}</strong>.</p>
          </div>
          {errors.purpose && <div className="submit-error">{errors.purpose}</div>}
        </div>
      )}

      <div className="form-nav">
        {step < 2
          ? <button className="btn btn--primary btn--full" onClick={handleNext}>Continue <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></button>
          : <button className="btn btn--primary btn--full" onClick={handleSubmit} disabled={loading}>
            {loading ? <><div className="spinner-sm" />Submitting...</> : <>Submit Request <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5 9-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></>}
          </button>
        }
      </div>
    </div>
  );
};

const RR: React.FC<{ label: string; value: string; capitalize?: boolean }> = ({ label, value, capitalize }) => (
  <div className="review-row">
    <span className="review-label">{label}</span>
    <span className={`review-value ${capitalize ? 'capitalize' : ''}`}>{value}</span>
  </div>
);

export default PublicRequestForm;